import { apiWrapper } from "@ch/api";
import { UsersControllerService, type UsersWithRelations } from "@ch/generated";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { handleError } from "../utils/handleError";
import { NotifyError } from "../utils/helpers";
import useLoading from "./useLoading";

interface TInitiateGetUser {
  userId: string;
  onSuccess?: (data: UsersWithRelations) => void;
  onFailure?: () => void;
}

const useGetUser = () => {
  const { loading, startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();

  const initiateGetUser = useCallback(
    async ({ onSuccess, onFailure, userId }: TInitiateGetUser) => {
      startLoading();
      try {
        if (!userId) {
          NotifyError("Invalid user ID");
          return;
        }

        const data = (await apiWrapper(() =>
          UsersControllerService.usersControllerFindById({
            id: userId,
          }),
        )) as unknown as UsersWithRelations;

        if (!data) {
          NotifyError("User data not found");
          return;
        }

        dispatch(
          updateAppUserState({
            currentUser: data,
          }),
        );

        onSuccess?.(data);
      } catch (error) {
        handleError(error);
        onFailure?.();
      } finally {
        stopLoading();
      }
    },
    [dispatch, startLoading, stopLoading],
  );

  return {
    loading,
    initiateGetUser,
  };
};

export default useGetUser;
